import { validationLength } from "../components/actions/validations";

/**
 * Default helper text shown under fields (non-error styling).
 * Error messages stay in `validationMessages` in validations.ts.
 */
export const fieldValidationHelperText = {
  outletName: `Max ${validationLength.outletName} characters; letters, numbers and spaces only; no special characters.`,
  categoryGroup: `Max ${validationLength.categoryGroup} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail Categories — category name (special characters allowed; no line breaks). */
  categoryName: `Max ${validationLength.golfCategoryName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail sub category name levels (special characters allowed; no line breaks). */
  subCategoryNameLevel: `Max ${validationLength.subCategoryName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Taxes — Tax Name (same rules/length as `validationLength.taxName`). */
  taxName: `Max ${validationLength.taxName} characters; letters, numbers and spaces only; no special characters.`,
  /** Discount type name (Retail / Golf; same rules/length as `validationLength.discountTypes`). */
  discountType: `Max ${validationLength.discountTypes} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail Discount Reason — special characters allowed; no line breaks (same max as `validationLength.discountReason`). */
  retailDiscountReason: `Max ${validationLength.discountReason} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Vendor Type — special characters allowed; no line breaks (same max as `validationLength.vendorType`). */
  retailVendorType: `Max ${validationLength.vendorType} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Vendor Setup — vendor type (same rules as retail vendor type). */
  retailVendorSetupVendorType: `Max ${validationLength.vendorType} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Vendor Setup — vendor code (letters and numbers only; no spaces). */
  retailVendorSetupVendorCode: `Max ${validationLength.vendorCode} characters; letters and numbers only; no spaces or special characters.`,
  /** Retail Vendor Setup — vendor name. */
  retailVendorSetupVendorName: `Max ${validationLength.vendorName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Vendor Setup — first name (no special characters). */
  retailVendorSetupFirstName: `Max ${validationLength.firstName} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail Vendor Setup — last name (no special characters). */
  retailVendorSetupLastName: `Max ${validationLength.lastName} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail Vendor Setup — address / city / state / country style text. */
  retailVendorSetupAddressField: `Max ${validationLength.buildingName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Vendor Setup — postal code. */
  retailVendorSetupPostalCode: `Max ${validationLength.centralReservation} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail Vendor Setup — phone / email type label fields. */
  retailVendorSetupPhoneEmailType: `Max ${validationLength.lessonLocation} characters; letters, numbers and spaces only; no special characters.`,
  retailVendorSetupCountryCode: "Digits only (max 4).",
  retailVendorSetupPhoneNumber: "Digits only (max 15).",
  retailVendorSetupEmailAddress:
    "Enter a valid email address (e.g. user@example.com).",
  /** Retail Vendor Setup — comments and terms (multiline). */
  retailVendorSetupMultiline: `Max ${validationLength.cancellationPolicy} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Quick Sale Category — special characters allowed; no line breaks (max `validationLength.quickSaleCategory`). */
  retailQuickSaleCategory: `Max ${validationLength.quickSaleCategory} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Role Setup — role code (max `validationLength.roleCode`). */
  retailRoleSetupCode: `Max ${validationLength.roleCode} characters; special characters allowed; line breaks not allowed.`,
  /** Retail Role Setup — role name (max `validationLength.retailRoleName`). */
  retailRoleSetupName: `Max ${validationLength.retailRoleName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail User Setup — first name. */
  retailUserSetupFirstName: `Max ${validationLength.firstName} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail User Setup — last name. */
  retailUserSetupLastName: `Max ${validationLength.lastName} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail User Setup — User ID. */
  retailUserSetupUserId: `Max ${validationLength.retailUserId} characters; letters and numbers only; no spaces or special characters.`,
  /** Retail User Setup — Quick ID (optional). */
  retailUserSetupQuickId: `Up to ${validationLength.retailUserSetupQuickIdDigits} digits only (optional).`,
  /** Retail User Setup — email (optional). */
  retailUserSetupEmail:
    "Enter a valid email address (e.g. user@example.com). Optional.",
  retailUserSetupLanguage: "Select language (English only at this time).",
  retailUserSetupPasswordExpiration:
    "Optional. Select when the password should expire.",
  /** Retail User Setup — new password (mandatory on add/update). */
  retailUserSetupNewPassword: `Max ${validationLength.retailUserSetupPassword} characters; special characters allowed; line breaks not allowed.`,
  /** Retail User Setup — application access (optional). */
  retailUserSetupApplicationAccess: `Max ${validationLength.outletName} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail User Setup — commission class (optional). */
  retailUserSetupCommissionClass: `Max ${validationLength.outletName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail User Setup — outlet access (mandatory). */
  retailUserSetupOutletAccess: `Max ${validationLength.outletName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail User Setup — role name linked to user (mandatory). */
  retailUserSetupRoleName: `Max ${validationLength.retailRoleName} characters; special characters allowed; line breaks not allowed.`,
  /** Retail User Setup — auto log-off minutes (optional). */
  retailUserSetupLogOffMinutes: "Up to 4 digits (optional).",

  /** Retail Items — item number (mandatory; digits only). */
  retailRetailItemNumber: `Digits only; max ${validationLength.postTypeNumber} characters (mandatory).`,
  /** Retail Items — item description (mandatory). */
  retailRetailItemDescription: `Max ${validationLength.itemDescription} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail Items — category (mandatory). */
  retailRetailItemCategory: `Max ${validationLength.golfCategoryName} characters; letters, numbers and spaces only; no special characters.`,
  /** Retail Items — sub categories (optional). */
  retailRetailItemSubCategory: `Max ${validationLength.subCategoryName} characters; letters, numbers and spaces only; no special characters (optional).`,
  /** Retail Items — unit of measure (optional). */
  retailRetailItemUnitOfMeasure: `Max ${validationLength.buildingName} characters; special characters allowed; line breaks not allowed (optional).`,
  /** Retail Items — sales price (mandatory). */
  retailRetailItemSalesPrice:
    "Enter a valid price (numbers; optional decimal). Mandatory.",
  /** Retail Items — bar code (optional). */
  retailRetailItemBarCode:
    "Optional. Digits only. If provided, must match Item Number.",
  /** Retail Items — secondary bar code (optional). */
  retailRetailItemSecondaryBarCode: `Max ${validationLength.buildingName} characters; special characters allowed; line breaks not allowed (optional).`,
  /** Retail Items — tax (optional). */
  retailRetailItemTax:
    "Optional. Select a tax from the list or type to filter.",
  /** Retail Items — item type (optional). */
  retailRetailItemItemType: "Optional. Typically 1; numbers only.",
  /** Retail Items — commission percent (optional). */
  retailRetailItemCommissionPercent:
    "Optional. Enter a valid number (optional decimal).",
  /** Retail Items — 0/1 numeric flags (optional). */
  retailRetailItemZeroOneNumeric: "Optional. 0 or 1 (numbers only).",
  /** Retail Items — numeric / key fields (optional). */
  retailRetailItemOptionalNumeric:
    "Optional. Enter a valid number (optional decimal).",
  /** Retail Items — digits-only optional fields. */
  retailRetailItemOptionalDigits: "Optional. Whole numbers (digits only).",
  /** Retail Items — outlet code (optional). */
  retailRetailItemOutletCode: `Max ${validationLength.outletName} characters; letters, numbers and spaces only; no special characters (optional).`,
  /** Retail Items — item par (optional). */
  retailRetailItemItemPar: "Optional. Whole numbers (digits only).",
  /** Retail Items — vendor item # (optional). */
  retailRetailItemVendorItemNo: `Max ${validationLength.vendorCode} characters; letters and numbers only; no spaces or special characters (optional).`,
  /** Retail Items — vendor name (optional). */
  retailRetailItemVendorName:
    "Optional. Select or search vendor from the list.",
} as const;
